/**
 * Login-gate helpers for checkout entry/exit (TK-12328).
 *
 * Capacitor can peek the send stack for the path under `/checkout`. Web has no stack, so
 * personalize remembers the entry URL in sessionStorage before auth/navigate.
 */

export const CHECKOUT_ENTRY_PATH_STORAGE_KEY = 'checkout_entry_path'

export function rememberCheckoutEntryPath(path: string): void {
    if (typeof sessionStorage === 'undefined') {
        return
    }
    const trimmed = path.trim()
    if (!trimmed) {
        return
    }
    sessionStorage.setItem(CHECKOUT_ENTRY_PATH_STORAGE_KEY, trimmed)
}

/** Reads remembered entry without clearing (mount guard + logout can share). */
export function peekCheckoutEntryPath(): string | null {
    if (typeof sessionStorage === 'undefined') {
        return null
    }
    const value = sessionStorage.getItem(CHECKOUT_ENTRY_PATH_STORAGE_KEY)?.trim()
    return value || null
}

export function clearCheckoutEntryPath(): void {
    if (typeof sessionStorage === 'undefined') {
        return
    }
    sessionStorage.removeItem(CHECKOUT_ENTRY_PATH_STORAGE_KEY)
}

/**
 * Where to send the user after logout while on `/checkout`.
 * Prefer the path under `/checkout` on the Capacitor send stack, then remembered entry, else `/`.
 * Returns null when not on checkout (caller keeps default logout behavior).
 */
export function resolveCheckoutLogoutRedirect(args: {
    currentPath: string
    stackPaths: readonly string[]
    entryPath?: string | null
}): string | null {
    if (args.currentPath !== '/checkout') {
        return null
    }

    const stackPaths = args.stackPaths
    if (stackPaths.length >= 2 && stackPaths[stackPaths.length - 1] === '/checkout') {
        const prior = stackPaths[stackPaths.length - 2]?.trim()
        if (prior) {
            return prior
        }
    }

    const entry = args.entryPath?.trim()
    if (entry) {
        return entry
    }

    return '/'
}

/** Defensive signed-out mount redirect: entry page if known, otherwise home. */
export function resolveSignedOutCheckoutRedirect(entryPath?: string | null): string {
    const entry = entryPath?.trim()
    return entry || '/'
}
